/**
 * Hook to fetch web/marketing metrics data
 */

import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useSalonStore } from "@/lib/store/salon";
import { GET_COMPREHENSIVE_REPORT } from "../graphql/report-queries";
import { formatReportDate } from "../lib/report-date-range";
import { useReportsV2Store } from "../store/use-reports-v2-store";
import { createComparableValue } from "../types";
import type { WebData } from "../types/web-types";

export function useWebData() {
  const { currentSalon } = useSalonStore();
  const { currentPeriod, comparisonPeriod } = useReportsV2Store();

  // Fetch current period data
  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
  } = useQuery(GET_COMPREHENSIVE_REPORT, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        period: "CUSTOM",
        startDate: currentPeriod
          ? formatReportDate(currentPeriod.startDate, currentSalon?.timezone || "UTC")
          : undefined,
        endDate: currentPeriod
          ? formatReportDate(currentPeriod.endDate, currentSalon?.timezone || "UTC")
          : undefined,
        timezone: currentSalon?.timezone || "UTC",
      },
    },
    skip: !currentSalon || !currentSalon.timezone || !currentPeriod,
  });

  // Fetch comparison period data
  const {
    data: comparisonData,
    loading: comparisonLoading,
    error: comparisonError,
  } = useQuery(GET_COMPREHENSIVE_REPORT, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        period: "CUSTOM",
        startDate: comparisonPeriod
          ? formatReportDate(comparisonPeriod.startDate, currentSalon?.timezone || "UTC")
          : undefined,
        endDate: comparisonPeriod
          ? formatReportDate(comparisonPeriod.endDate, currentSalon?.timezone || "UTC")
          : undefined,
        timezone: currentSalon?.timezone || "UTC",
      },
    },
    skip: !currentSalon || !currentSalon.timezone || !comparisonPeriod,
  });

  const loading = currentLoading || comparisonLoading;
  const error = currentError || comparisonError;
  const isReady = !(!currentSalon || !currentSalon.timezone || !currentPeriod || !comparisonPeriod);

  // Transform GraphQL data into web format
  const webData: WebData | null = useMemo(() => {
    if (!isReady) return null;
    if (!currentData?.comprehensiveReport || !comparisonData?.comprehensiveReport) return null;

    const current = currentData.comprehensiveReport;
    const comparison = comparisonData.comprehensiveReport;

    // Return null if marketing data is not available (non-premium)
    if (!current.marketing || !comparison.marketing) return null;

    return {
      web: {
        totalInquiries: {
          type: "absolute",
          label: "Total Inquiries",
          value: createComparableValue(
            current.marketing.totalInquiries,
            comparison.marketing.totalInquiries
          ),
          format: "number",
        },
        totalBookings: {
          type: "absolute",
          label: "Total Bookings",
          value: createComparableValue(
            current.marketing.totalBookings,
            comparison.marketing.totalBookings
          ),
          format: "number",
        },
        overallConversionRate: {
          type: "absolute",
          label: "Overall Conversion Rate",
          value: createComparableValue(
            current.marketing.overallConversionRate,
            comparison.marketing.overallConversionRate,
            false,
            true // isPercentageValue
          ),
          format: "percentage",
        },
        trafficSources: {
          current: current.marketing.trafficSources || [],
          previous: comparison.marketing.trafficSources || [],
        },
      },
    };
  }, [currentData, comparisonData, isReady]);

  if (!isReady) {
    return { data: null, loading: false, error: null };
  }

  return {
    data: webData,
    loading,
    error,
  };
}
